import { useState, useRef, useEffect } from 'react';
import { Upload, X, FileText, Image, Loader2, CheckCircle, AlertCircle, ChevronDown, Lock } from 'lucide-react';
import { transactionAPI } from '../api/client';
import { useTokens } from '../context/ThemeContext';

let nextId = 1;

function UploadPanel({ onUploadComplete, openOnMount = false, onOpened }) {
  const t = useTokens();
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (openOnMount) setExpanded(true);
  }, [openOnMount]); // eslint-disable-line
  const [items, setItems] = useState([]); // { id, name, status, result, error }
  const [passwordPrompt, setPasswordPrompt] = useState(null); // { id, file }
  const [password, setPassword] = useState('');
  const fileInputRef = useRef(null);

  const updateItem = (id, patch) =>
    setItems(prev => prev.map(it => it.id === id ? { ...it, ...patch } : it));

  const runUpload = async (id, file, pwd = null) => {
    const formData = new FormData();
    formData.append('statement', file);
    if (pwd) formData.append('password', pwd);
    try {
      const res = await transactionAPI.uploadStatement(formData);
      updateItem(id, { status: 'success', result: res.data });
      if (onUploadComplete) onUploadComplete();
    } catch (err) {
      const errCode = err.response?.data?.error;
      if (errCode === 'PASSWORD_REQUIRED') {
        updateItem(id, { status: 'password' });
        setPasswordPrompt({ id, file });
        setPassword('');
      } else {
        updateItem(id, { status: 'error', error: err.response?.data?.message || 'Upload failed' });
      }
    }
  };

  const handleFiles = (files) => {
    setExpanded(true);
    Array.from(files).forEach(file => {
      const id = nextId++;
      setItems(prev => [...prev, { id, name: file.name, status: 'uploading' }]);
      runUpload(id, file);
    });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileInput = (e) => {
    if (e.target.files?.length) handleFiles(e.target.files);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files?.length) handleFiles(e.dataTransfer.files);
  };

  const handlePasswordSubmit = async () => {
    if (!passwordPrompt || !password) return;
    const { id, file } = passwordPrompt;
    updateItem(id, { status: 'uploading' });
    setPasswordPrompt(null);
    setPassword('');
    await runUpload(id, file, password);
  };

  const removeItem = (id) => setItems(prev => prev.filter(it => it.id !== id));
  const clearAll = () => setItems([]);

  const activeCount = items.filter(it => it.status === 'uploading').length;
  const totalCount = items.length;

  const statusIcon = (item) => {
    if (item.status === 'uploading') return <Loader2 size={14} style={{ animation: 'spin 1s linear infinite', color: t.textSlate, flexShrink: 0 }} />;
    if (item.status === 'success') return <CheckCircle size={14} style={{ color: '#16a34a', flexShrink: 0 }} />;
    if (item.status === 'error') return <AlertCircle size={14} style={{ color: '#dc2626', flexShrink: 0 }} />;
    if (item.status === 'password') return <Lock size={14} style={{ color: '#d97706', flexShrink: 0 }} />;
    return null;
  };

  const statusText = (item) => {
    if (item.status === 'uploading') return 'Processing…';
    if (item.status === 'success') {
      const n = item.result?.newTransactionsSaved ?? item.result?.transactionsSaved ?? 0;
      return `${n} transaction${n !== 1 ? 's' : ''} imported`;
    }
    if (item.status === 'error') return item.error;
    if (item.status === 'password') return 'Password required';
    return '';
  };

  // Hide entirely only when no uploads exist and the panel is closed
  if (totalCount === 0 && !expanded) return null;

  return (
    <div style={{ position: 'fixed', bottom: '80px', right: 0, zIndex: 1200, display: 'flex', alignItems: 'flex-end', flexDirection: 'row' }}>
      {/* Expanded panel — sits to the left of the tab */}
      {expanded && (
        <div style={{
          width: '300px',
          backgroundColor: t.surface,
          borderRadius: '12px 0 0 12px',
          boxShadow: '-4px 4px 20px rgba(0,0,0,0.15)',
          border: `1px solid ${t.border}`,
          borderRight: 'none',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '420px',
          overflow: 'hidden',
        }}>
          {/* Panel header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', borderBottom: `1px solid ${t.borderLight}` }}>
            <span style={{ fontSize: '13px', fontWeight: '600', color: t.text }}>
              Upload Statements
              {activeCount > 0 && <span style={{ marginLeft: '8px', fontSize: '11px', color: '#d97706', fontWeight: '500' }}>{activeCount} processing…</span>}
            </span>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              {items.length > 0 && (
                <button onClick={clearAll} style={{ fontSize: '11px', color: t.textMuted, background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px' }}>Clear all</button>
              )}
              <button onClick={() => setExpanded(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: t.textMuted, display: 'flex' }}>
                <ChevronDown size={16} />
              </button>
            </div>
          </div>

          {/* Drop zone */}
          <div
            onDragOver={e => e.preventDefault()}
            onDrop={handleDrop}
            style={{ padding: '12px 14px', borderBottom: `1px solid ${t.borderLight}` }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.csv,.jpg,.jpeg,.png,.webp,.heic,.heif"
              multiple
              onChange={handleFileInput}
              style={{ display: 'none' }}
              id="upload-panel-input"
            />
            <label
              htmlFor="upload-panel-input"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                padding: '10px',
                border: `1.5px dashed ${t.border}`,
                borderRadius: '8px',
                cursor: 'pointer',
                backgroundColor: t.inputBg,
                fontSize: '13px',
                color: t.textSecondary,
              }}
            >
              <FileText size={15} color={t.textMuted} />
              <Image size={15} color={t.textMuted} />
              <span>Click or drop files to upload</span>
            </label>
            <p style={{ fontSize: '11px', color: t.textMuted, margin: '6px 0 0 0', textAlign: 'center' }}>
              PDF, CSV, JPG, PNG, WEBP, HEIC · Max 20MB
            </p>
          </div>

          {/* Password prompt (inline) */}
          {passwordPrompt && (
            <div style={{ padding: '12px 14px', borderBottom: `1px solid ${t.borderLight}`, backgroundColor: '#fffbeb' }}>
              <p style={{ fontSize: '12px', color: '#92400e', margin: '0 0 8px 0' }}>
                <Lock size={12} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                Password required for <strong>{items.find(i => i.id === passwordPrompt.id)?.name}</strong>
              </p>
              <div style={{ display: 'flex', gap: '6px' }}>
                <input
                  type="password"
                  placeholder="PDF password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handlePasswordSubmit()}
                  autoFocus
                  style={{ flex: 1, padding: '6px 10px', border: `1px solid ${t.inputBorder}`, borderRadius: '6px', fontSize: '13px', backgroundColor: t.inputBg, color: t.text, outline: 'none' }}
                />
                <button
                  onClick={handlePasswordSubmit}
                  disabled={!password}
                  style={{ padding: '6px 10px', backgroundColor: t.textSlate, color: 'white', border: 'none', borderRadius: '6px', fontSize: '12px', cursor: password ? 'pointer' : 'not-allowed', opacity: password ? 1 : 0.5 }}
                >
                  Unlock
                </button>
              </div>
            </div>
          )}

          {/* Upload list */}
          {items.length > 0 && (
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {items.map(item => (
                <div key={item.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '10px 14px', borderBottom: `1px solid ${t.borderLight}` }}>
                  {statusIcon(item)}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: 0, fontSize: '12px', fontWeight: '500', color: t.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.name}</p>
                    <p style={{ margin: '2px 0 0 0', fontSize: '11px', color: item.status === 'error' ? '#dc2626' : item.status === 'success' ? '#16a34a' : item.status === 'password' ? '#d97706' : t.textMuted }}>
                      {statusText(item)}
                    </p>
                  </div>
                  {(item.status === 'success' || item.status === 'error') && (
                    <button onClick={() => removeItem(item.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: t.textMuted, padding: 0, display: 'flex', flexShrink: 0 }}>
                      <X size={13} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Vertical tab — right edge */}
      <button
        onClick={() => setExpanded(v => !v)}
        title={expanded ? 'Collapse uploads' : 'Show uploads'}
        style={{
          writingMode: 'vertical-rl',
          textOrientation: 'mixed',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '14px 8px',
          backgroundColor: t.textSlate,
          color: 'white',
          border: 'none',
          borderRadius: '8px 0 0 8px',
          cursor: 'pointer',
          fontSize: '12px',
          fontWeight: '600',
          letterSpacing: '0.03em',
          boxShadow: '-2px 0 8px rgba(0,0,0,0.15)',
          userSelect: 'none',
          flexShrink: 0,
        }}
      >
        <Upload size={14} style={{ flexShrink: 0 }} />
        <span style={{
          backgroundColor: activeCount > 0 ? '#f59e0b' : '#16a34a',
          color: 'white',
          borderRadius: '999px',
          padding: '1px 5px',
          fontSize: '10px',
          fontWeight: '700',
          display: 'inline-block',
        }}>{totalCount}</span>
        Uploads
      </button>
    </div>
  );
}

export default UploadPanel;
